import { useEffect } from 'react';

const SITE_URL = 'https://ramirezmobilecleantruck.com';
const SITE_NAME = 'Ramirez Mobile Clean Truck';
const DEFAULT_IMAGE = 'https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/f2db29be14ca2eb40f068da8d183a853.jpeg';

const setMeta = (selector, attribute, value) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }

  element.setAttribute('content', value);
};

const formatPageName = (route) => {
  if (!route || route === '/') return 'Home';

  return route
    .split('/')
    .filter(Boolean)
    .map((part) => part.replace(/-/g, ' '))
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' / ');
};

const useSEO = ({
  title,
  description,
  canonicalRoute = '/',
  keywords = '',
  image = DEFAULT_IMAGE,
  type = 'website'
}) => {
  useEffect(() => {
    const cleanRoute = canonicalRoute.split('?')[0] || '/';
    const canonicalUrl = `${SITE_URL}${cleanRoute}`;
    const fullTitle = title?.includes(SITE_NAME)
      ? title
      : `${title} | ${SITE_NAME}`;
    const pageName = formatPageName(cleanRoute);

    document.title = fullTitle;

    setMeta('meta[name="description"]', 'name', description);
    setMeta('meta[name="keywords"]', 'name', keywords);
    setMeta('meta[name="author"]', 'name', SITE_NAME);
    setMeta('meta[name="robots"]', 'name', 'index,follow,max-image-preview:large');
    setMeta('meta[property="og:title"]', 'property', fullTitle);
    setMeta('meta[property="og:description"]', 'property', description);
    setMeta('meta[property="og:type"]', 'property', type);
    setMeta('meta[property="og:url"]', 'property', canonicalUrl);
    setMeta('meta[property="og:image"]', 'property', image);
    setMeta('meta[property="og:site_name"]', 'property', SITE_NAME);
    setMeta('meta[name="twitter:card"]', 'name', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name', fullTitle);
    setMeta('meta[name="twitter:description"]', 'name', description);
    setMeta('meta[name="twitter:image"]', 'name', image);

    let canonical = document.head.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }

    canonical.href = canonicalUrl;

    const schemaId = 'dynamic-page-schema';
    const previousSchema = document.getElementById(schemaId);

    if (previousSchema) {
      previousSchema.remove();
    }

    const schema = document.createElement('script');
    schema.id = schemaId;
    schema.type = 'application/ld+json';
    schema.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${canonicalUrl}#webpage`,
          url: canonicalUrl,
          name: fullTitle,
          description,
          isPartOf: {
            '@id': `${SITE_URL}/#website`
          },
          about: {
            '@id': `${SITE_URL}/#business`
          },
          inLanguage: 'en-US'
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${canonicalUrl}#breadcrumbs`,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: `${SITE_URL}/`
            },
            ...(cleanRoute === '/'
              ? []
              : [{
                  '@type': 'ListItem',
                  position: 2,
                  name: pageName,
                  item: canonicalUrl
                }])
          ]
        }
      ]
    });

    document.head.appendChild(schema);

    return () => {
      const activeSchema = document.getElementById(schemaId);

      if (activeSchema) {
        activeSchema.remove();
      }
    };
  }, [title, description, canonicalRoute, keywords, image, type]);
};

export default useSEO;